import React, { FormEvent, useRef, useState } from 'react'
import Input from '@/components/ContentElements/Forms/Fields/Input'
import Text from '@/components/typographie/Text/Text'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'

export interface FormProps {
  children?: React.ReactNode
  className?: string
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
}

export default function NewsletterForm({
  children,
  className,
  setIsSubmitting
}: FormProps) {
  const [message, setMessage] = useState({ status: 0, text: '' })
  const t = useTranslations('Forms')
  const ref = useRef<HTMLFormElement>(null)
  const [honeypotValue, setHoneypotValue] = useState<string>('')
  const formLoadTime = useRef(Date.now())

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setMessage({ status: 0, text: '' })
    setIsSubmitting(true)

    // Honeypot check (client-side fast fail)
    if (honeypotValue.trim().length > 0) {
      console.error('Bot detected - honeypot filled')
      setIsSubmitting(false)
      setMessage({ status: 200, text: t('NewsletterForm.Mailing.success') })
      ref.current?.reset()
      setHoneypotValue('')
      return
    }

    // Time-based check (client-side fast fail)
    const timeTaken = Date.now() - formLoadTime.current
    if (timeTaken < 3000) {
      console.error('Bot detected - too fast')
      setIsSubmitting(false)
      setMessage({ status: 200, text: t('NewsletterForm.Mailing.success') })
      ref.current?.reset()
      setHoneypotValue('')
      return
    }

    const formData = new FormData(event.currentTarget)
    const formValues = Object.fromEntries(formData.entries())

    try {
      const res = await fetch('/corporate/api/newsletter/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formValues,
          hp: honeypotValue,
          ts: formLoadTime.current
        })
      })

      // API always returns { ok: true } to avoid giving bots feedback, but we still treat non-2xx as error.
      if (res.ok) {
        setMessage({ status: 200, text: t('NewsletterForm.Mailing.success') })
      } else {
        setMessage({ status: 500, text: t('NewsletterForm.Mailing.error') })
      }
    } catch (err) {
      console.error(err)
      setMessage({ status: 500, text: t('NewsletterForm.Mailing.error') })
    } finally {
      ref.current?.reset()
      setHoneypotValue('')
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* Honeypot outside the <form> to avoid edge autofill submitting it */}
      <input
        type="text"
        name="xyz_asdf"
        tabIndex={-1}
        autoComplete="off"
        value={honeypotValue}
        onChange={(e) => setHoneypotValue(e.target.value)}
        className="absolute -left-[5000px]"
        aria-hidden="true"
      />

      <form onSubmit={onSubmit} ref={ref} className={className}>
        {message.text && (
          <p
            className={clsx(
              message.status === 200 ? 'text-success' : 'text-error',
              'font-sans text-h2'
            )}
          >
            {message.text}
          </p>
        )}
        <fieldset className="flex flex-wrap gap-l md:gap-xl">
          <legend className="mb-s w-full">
            <Text>{t('NewsletterForm.interests')}</Text>
          </legend>
          <Input
            label={t('NewsletterForm.allNews')}
            type="radio"
            name="newsletter"
            value="all_news"
            isRequired={true}
            checked={true}
          />
          <Input
            label="Corporate News"
            type="radio"
            name="newsletter"
            value="corporate_news"
            isRequired={true}
          />
          <Input
            label="Fashion News"
            type="radio"
            name="newsletter"
            value="fashion_news"
            isRequired={true}
          />
        </fieldset>
        <Input
          label={t('Labels.firstName')}
          type="text"
          palceholder={t('Placeholder.yourFirstName')}
          name="first_name"
          isRequired={true}
        />
        <Input
          label={t('Labels.lastName')}
          type="text"
          palceholder={t('Placeholder.yourLastName')}
          name="last_name"
          isRequired={true}
        />
        <Input
          label={t('Labels.employer')}
          type="text"
          palceholder={t('Placeholder.yourEmployer')}
          name="publisher_editorial"
          isRequired={true}
        />
        <Input
          label={t('Labels.email')}
          type="email"
          palceholder={t('Placeholder.emailAddress')}
          name="email"
          isRequired={true}
        />
        {children}
      </form>
    </>
  )
}
